// BUILD-046 pass F1 (Owner direction 2026-10-25, item 2): the ORIGINAL requester shown in Edit task and Update task, whichever tab opened the form and whatever the list's "Show requester" display setting says.
//
// What it reads is only what the task the form was opened on already carries for this viewer: `requesterName` (the person who issued the request, as the server projected it - the most recent editor and the
// current viewer are never substituted) and `createdAt` (when the server recorded the request, not when it was last modified). A task shown in its restricted form, or one that carries no requester (an older task
// created before requesters were recorded), yields null: nothing is invented and nothing is widened. Erasable TypeScript, no imports.
export interface RequesterContext {
  /** The stored full name; the page draws it with PersonName (short form, full name on hover or focus). */
  name: string;
  /** "Oct 22" (the year is added when it is not the current one), or '' when the request has no readable recorded date. */
  requestedOn: string;
}

/** The day the request was recorded, on the Phoenix clock. '' when it cannot be read. */
export function requestedOnText(iso: unknown, now: Date = new Date()): string {
  if (typeof iso !== 'string') return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const zone = 'America/Phoenix';
  const year = (date: Date): string => new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric' }).format(date);
  const when = new Date(ms);
  const withYear = year(when) !== year(now);
  return new Intl.DateTimeFormat('en-US', { timeZone: zone, month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' as const } : {}) }).format(when);
}

export function requesterContext(item: unknown, now: Date = new Date()): RequesterContext | null {
  if (item === null || typeof item !== 'object') return null;
  const raw = item as { requesterName?: unknown; createdAt?: unknown; redacted?: unknown };
  if (raw.redacted === true) return null;
  const name = typeof raw.requesterName === 'string' ? raw.requesterName.trim() : '';
  if (name === '') return null;
  return { name, requestedOn: requestedOnText(raw.createdAt, now) };
}

/** One or two capital letters for a person's circle ("Julie Manager (FICTIONAL)" is JM). '?' when there is no readable name. */
export function initialsOf(name: unknown): string {
  const words = String(name ?? '').replace(/\([^)]*\)/g, ' ').split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word));
  if (words.length === 0) return '?';
  const first = Array.from(words[0])[0] ?? '?';
  const last = words.length > 1 ? Array.from(words[words.length - 1])[0] ?? '' : '';
  return (first + last).toUpperCase();
}
